import { Filter, RotateCcw } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const ExpensesFiltersDeck = ({
  summary,
  filteredExpensesCount,
  activeStatusLabel,
  visibleExpenseAmount,
  activeExpenseFilterChips = [],
  searchQuery,
  onSearchChange,
  periodValue,
  onPeriodChange,
  periodLabels,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
  selectedLocationIds = [],
  onLocationChange,
  locations = [],
  selectedStatusIds = [],
  onStatusChange,
  statusOptions = [],
  statusLabels,
  selectedCategoryIds = [],
  onCategoryChange,
  categoryFilterOptions = [],
  selectedSubcategoryIds = [],
  onSubcategoryChange,
  quickFilterSubcategories = [],
  selectedPaymentAccountIds = [],
  onPaymentAccountChange,
  paymentAccounts = [],
  onResetFilters,
  actionsSlot = null,
}) => {
  const locationValue = selectedLocationIds[0] || 'all';
  const statusValue = selectedStatusIds[0] || 'all';
  const categoryValue = selectedCategoryIds[0] || 'all';
  const subcategoryValue = selectedSubcategoryIds[0] || 'all';
  const paymentAccountValue = selectedPaymentAccountIds[0] || 'all';

  return (
  <>
    <Card className="border-slate-200 shadow-sm" data-testid="expenses-filters-deck">
      <CardContent className="space-y-3 p-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Filter className="h-4 w-4 text-slate-500" />
            Filtros de despesas
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="expenses-active-filters-strip">
            <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">{activeStatusLabel}</Badge>
            <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">{filteredExpensesCount} registos</Badge>
            <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">{formatCurrency(visibleExpenseAmount)}</Badge>
            {activeExpenseFilterChips.length > 0 ? activeExpenseFilterChips.map((chip) => (
              <Badge key={chip.key} variant="outline" className="rounded-full px-3 py-1 text-slate-600" data-testid={`expenses-active-filter-chip-${chip.key}`}>
                {chip.label}
              </Badge>
            )) : (
              <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-600" data-testid="expenses-active-filter-chip-default">
                Todo período
              </Badge>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="expenses-primary-filters-panel">
          <div className="hidden xl:flex xl:flex-nowrap xl:items-end xl:gap-1.5" data-testid="expenses-filters-inline-layout">
            <div className="min-w-0 flex-[1.15]" data-testid="expenses-filter-block-search">
              <Label htmlFor="expense-search-filter" className="text-[11px] text-muted-foreground">Pesquisa</Label>
              <Input id="expense-search-filter" value={searchQuery} onChange={(event) => onSearchChange(event.target.value)} placeholder="Código, descrição, artigo, fornecedor ou referência" data-testid="expense-search-filter" className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" />
            </div>

            <div className="min-w-0 flex-[0.92]" data-testid="expenses-filter-block-period">
              <Label className="text-[11px] text-muted-foreground">Período</Label>
              <div className="mt-1 flex items-center gap-1.5">
                <Select value={periodValue} onValueChange={onPeriodChange}>
                  <SelectTrigger className="h-8 min-w-[118px] rounded-lg border-slate-200 bg-white text-[11px]" data-testid="expenses-toolbar-period-filter">
                    <SelectValue placeholder="Período" />
                  </SelectTrigger>
                  <SelectContent>
                    {['all', 'today', 'yesterday', 'this_week', 'this_month', 'last_month', 'this_year', 'last_year', 'custom'].map((periodKey) => (
                      <SelectItem key={periodKey} value={periodKey}>{periodLabels[periodKey]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {periodValue === 'custom' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="h-8 rounded-lg border-slate-200 bg-white px-2 text-[10px]" data-testid="expenses-custom-period-trigger">
                        Datas
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[280px] space-y-3" data-testid="expenses-custom-period-popover">
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Data inicial</Label>
                        <Input type="date" value={customStart || ''} onChange={(event) => onCustomStartChange(event.target.value)} className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="expenses-custom-period-start" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Data final</Label>
                        <Input type="date" value={customEnd || ''} onChange={(event) => onCustomEndChange(event.target.value)} className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="expenses-custom-period-end" />
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-[0.72]" data-testid="expenses-filter-block-location">
              <Label className="text-[11px] text-muted-foreground">Localização</Label>
              <Select value={locationValue} onValueChange={(value) => onLocationChange(value === 'all' ? null : value)}>
                <SelectTrigger className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="expenses-location-filter-select">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-0 flex-[0.58]" data-testid="expenses-filter-block-status">
              <Label className="text-[11px] text-muted-foreground">Estado</Label>
              <Select value={statusValue} onValueChange={(value) => onStatusChange(value === 'all' ? null : value)}>
                <SelectTrigger className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="expenses-status-filter-select">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {statusOptions.map((status) => <SelectItem key={status} value={status}>{statusLabels[status] || status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-0 flex-[1.38]" data-testid="expenses-filter-block-category-subcategory">
              <Label className="text-[11px] text-muted-foreground">Categoria / Subcategoria</Label>
              <div className="mt-1 flex items-center gap-1.5">
                <Select value={categoryValue} onValueChange={(value) => onCategoryChange(value === 'all' ? null : value)}>
                  <SelectTrigger className="h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="expenses-category-filter-select">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categoryFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={subcategoryValue} onValueChange={(value) => onSubcategoryChange(value === 'all' ? null : value)} disabled={categoryValue === 'all'}>
                  <SelectTrigger className="h-8 rounded-lg border-slate-200 bg-white text-[11px] disabled:opacity-50" data-testid="expenses-subcategory-filter-select">
                    <SelectValue placeholder="Subcategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {quickFilterSubcategories.map((subcategory) => <SelectItem key={subcategory.id} value={subcategory.id}>{subcategory.expense_code} · {subcategory.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="min-w-0 flex-[0.62]" data-testid="expenses-filter-block-account">
              <Label className="text-[11px] text-muted-foreground">Conta / caixa</Label>
              <Select value={paymentAccountValue} onValueChange={(value) => onPaymentAccountChange(value === 'all' ? null : value)}>
                <SelectTrigger className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="expenses-account-filter-select">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {paymentAccounts.map((account) => <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[140px] flex-[0.62]" data-testid="expenses-filter-block-actions">
              <Label className="sr-only">Acções</Label>
              <div className="mt-1 flex items-center justify-end gap-1">
                {actionsSlot}
                <Button type="button" variant="outline" className="h-8 w-8 rounded-lg border-slate-200 bg-white px-0 text-[10px]" onClick={onResetFilters} data-testid="expenses-reset-filters-button" title="Limpar filtros">
                  <RotateCcw className="h-4 w-4" />
                  <span className="sr-only">Limpar filtros</span>
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-3 xl:hidden" data-testid="expenses-mobile-filters-layout">
            <div>
              <Label htmlFor="expense-search-filter-mobile" className="text-[11px] text-muted-foreground">Pesquisa</Label>
              <Input id="expense-search-filter-mobile" value={searchQuery} onChange={(event) => onSearchChange(event.target.value)} placeholder="Código, descrição, artigo, fornecedor ou referência" data-testid="expense-search-filter-mobile" className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm" />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Período</Label>
              <div className="mt-1.5 grid gap-2">
                <Select value={periodValue} onValueChange={onPeriodChange}>
                  <SelectTrigger className="h-9 rounded-lg border-slate-200 bg-white text-sm" data-testid="expenses-toolbar-period-filter-mobile"><SelectValue placeholder="Período" /></SelectTrigger>
                  <SelectContent>
                    {['all', 'today', 'yesterday', 'this_week', 'this_month', 'last_month', 'this_year', 'last_year', 'custom'].map((periodKey) => (
                      <SelectItem key={periodKey} value={periodKey}>{periodLabels[periodKey]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {periodValue === 'custom' && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" value={customStart || ''} onChange={(event) => onCustomStartChange(event.target.value)} className="h-9 rounded-lg border-slate-200 bg-white text-sm" data-testid="expenses-custom-period-start-mobile" />
                    <Input type="date" value={customEnd || ''} onChange={(event) => onCustomEndChange(event.target.value)} className="h-9 rounded-lg border-slate-200 bg-white text-sm" data-testid="expenses-custom-period-end-mobile" />
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-[11px] text-muted-foreground">Localização</Label>
                <Select value={locationValue} onValueChange={(value) => onLocationChange(value === 'all' ? null : value)}>
                  <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todas</SelectItem>{locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Estado</Label>
                <Select value={statusValue} onValueChange={(value) => onStatusChange(value === 'all' ? null : value)}>
                  <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todos</SelectItem>{statusOptions.map((status) => <SelectItem key={status} value={status}>{statusLabels[status] || status}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Categoria</Label>
                <Select value={categoryValue} onValueChange={(value) => onCategoryChange(value === 'all' ? null : value)}>
                  <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todas</SelectItem>{categoryFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Subcategoria</Label>
                <Select value={subcategoryValue} onValueChange={(value) => onSubcategoryChange(value === 'all' ? null : value)} disabled={categoryValue === 'all'}>
                  <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm disabled:opacity-50"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todas</SelectItem>{quickFilterSubcategories.map((subcategory) => <SelectItem key={subcategory.id} value={subcategory.id}>{subcategory.expense_code} · {subcategory.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Conta / caixa</Label>
                <Select value={paymentAccountValue} onValueChange={(value) => onPaymentAccountChange(value === 'all' ? null : value)}>
                  <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todas</SelectItem>{paymentAccounts.map((account) => <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" className="h-9 rounded-lg border-slate-200 bg-white px-2 text-sm" onClick={onResetFilters} data-testid="expenses-reset-filters-button-mobile">
                <RotateCcw className="mr-2 h-4 w-4" />
                Limpar
              </Button>
              {actionsSlot}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </>
  );
};